import React, { useState, useEffect, useRef } from 'react';
import { useDragLayer } from 'react-dnd';
import { Box } from '@mui/material';
import OperatorCard from './OperatorCard';

const layerStyles = {
  position: 'fixed',
  pointerEvents: 'none',
  zIndex: 100,
  left: 0,
  top: 0,
};

function getItemStyles(offset, rotation) {
  if (!offset) {
    return { display: 'none' };
  }
  const { x, y } = offset;
  // Offset so the card appears to dangle below the cursor.
  const transform = `translate(${x - 120}px, ${y + 10}px) rotate(${rotation}deg)`;
  return {
    transform,
    WebkitTransform: transform,
    cursor: 'auto'
  };
}

const CustomDragLayer = () => {
  const { item, isDragging, currentOffset } = useDragLayer((monitor) => ({
    item: monitor.getItem(),
    isDragging: monitor.isDragging(),
    currentOffset: monitor.getClientOffset(),
  }));

  // State for the lagging preview position and rotation.
  const [previewPos, setPreviewPos] = useState(null);
  const [rotation, setRotation] = useState(0);

  const animationFrame = useRef(null);
  const damping = 0.15; // Damping factor for both position and rotation.
  const rotationFactor = 0.3; // Sensitivity factor for x displacement to rotation.

  useEffect(() => {
    if (!isDragging || !currentOffset) {
      setPreviewPos(null);
      setRotation(0);
      return;
    }

    const animate = () => {
      setPreviewPos((prevPos) => {
        // If no previous position, initialize it to the current cursor offset.
        if (!prevPos) return currentOffset;

        // Smoothly interpolate the position.
        const newX = prevPos.x + (currentOffset.x - prevPos.x) * damping;
        const newY = prevPos.y + (currentOffset.y - prevPos.y) * damping;

        // Compute only the x displacement.
        const deltaX = currentOffset.x - prevPos.x;
        const targetAngle = deltaX * rotationFactor;

        // Update rotation with an inversion, as per your suggestion.
        setRotation((prevRotation) => prevRotation + (targetAngle - prevRotation) * damping);

        return { x: newX, y: newY };
      });
      animationFrame.current = requestAnimationFrame(animate);
    };

    animationFrame.current = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(animationFrame.current);
  }, [isDragging, currentOffset]);

  if (!isDragging || !item) {
    return null;
  }

  return (
    <Box style={layerStyles}>
      <Box style={getItemStyles(previewPos, rotation)}>
        <OperatorCard operator={item.operator} isPreview />
      </Box>
    </Box>
  );
};

export default CustomDragLayer;
